import React, { useState, useEffect } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, UserCircle, Mail, Phone, Building2, Briefcase, Save, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const GET_ADMIN_PROFILE = gql`
  query GetCurrentAdmin {
    getCurrentAdmin {
      id
      adminId
      username
      firstName
      lastName
      email
      phone
      position
      department
      bio
      joinDate
      profileImage {
        data
        contentType
      }
      roles
    }
  }
`;

const UPDATE_ADMIN_PROFILE = gql`
  mutation UpdateAdminProfile($input: UpdateAdminInput!) {
    updateAdminProfile(input: $input) {
      id
      firstName
      lastName
      email
      phone
      position
      department
      bio
      joinDate
      profileImage {
        data
        contentType
      }
    }
  }
`;

const PersonnelProfilePage = () => {
  const { data: adminData, loading, refetch } = useQuery(GET_ADMIN_PROFILE);
  const [updateProfile] = useMutation(UPDATE_ADMIN_PROFILE);

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    bio: '',
    joinDate: '',
    profileImage: null,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());

  useEffect(() => {
    if (adminData?.getCurrentAdmin) {
      const admin = adminData.getCurrentAdmin;
      const joinDate = admin.joinDate ? new Date(parseInt(admin.joinDate)) : null;
      setProfile({
        firstName: admin.firstName || '',
        lastName: admin.lastName || '',
        email: admin.email || '',
        phone: admin.phone || '',
        position: admin.position || '',
        department: admin.department || '',
        bio: admin.bio || '',
        joinDate,
      });
      setCalendarDate(joinDate || new Date());

      if (admin.profileImage?.data) {
        setPreviewImage(`data:${admin.profileImage.contentType};base64,${admin.profileImage.data}`);
      }
    }
  }, [adminData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setProfile(prev => ({ ...prev, joinDate: date }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5000000) { // 5MB limit
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1];
      setPreviewImage(reader.result);
      setProfile(prev => ({
        ...prev,
        profileImage: {
          data: base64String,
          contentType: file.type
        }
      }));
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    try {
      const { firstName, lastName, email, phone, position, department, bio, joinDate, profileImage } = profile;

      const input = {
        firstName,
        lastName,
        email,
        phone,
        position,
        department,
        bio,
        joinDate: joinDate ? joinDate.getTime().toString() : null,
      };

      if (profileImage) {
        input.profileImage = profileImage;
      }

      await updateProfile({
        variables: { input }
      });

      toast.success('Profile updated successfully');
      setIsEditing(false);
      refetch();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'Chief_RPS': 'Chief RPS',
      'superadmin': 'Super Admin',
      'Technical_Staff': 'Technical Staff',
      'Forest_Ranger': 'Forest Ranger',
      'Chief_TSD': 'Chief TSD',
      'Receiving_Clerk': 'Receiving Clerk',
      'Releasing_Clerk': 'Releasing Clerk',
      'Accountant': 'Accountant',
      'OOP_Staff_Incharge': 'OOP Staff Incharge',
      'Bill_Collector': 'Bill Collector',
      'Credit_Officer': 'Credit Officer',
      'PENR_CENR_Officer': 'PENR/CENR Officer',
      'Deputy_CENR_Officer': 'Deputy CENR Officer',
      'Inspection_Team': 'Inspection Team'
    };

    return roleMap[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 p-6 lg:p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-8 pt-20">
        {/* Header Section */}
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Personnel Profile</h1>
          <p className="text-gray-600 dark:text-gray-300">
            View and manage your profile information
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Profile Card */}
          <div className="md:col-span-1">
            <Card className="dark:bg-black overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-800 h-32"></div>
              <CardContent className="p-6 -mt-16 relative">
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <Avatar className="h-24 w-24 border-4 border-white dark:border-gray-800">
                      {previewImage ? (
                        <AvatarImage src={previewImage} alt="Profile" />
                      ) : (
                        <AvatarFallback className="bg-green-100 text-green-800 text-2xl">
                          {profile.firstName && profile.lastName
                            ? `${profile.firstName[0]}${profile.lastName[0]}`
                            : <UserCircle className="h-12 w-12" />
                          }
                        </AvatarFallback>
                      )}
                    </Avatar>
                    {isEditing && (
                      <label
                        htmlFor="profile-image-upload"
                        className="absolute bottom-0 right-0 bg-green-600 rounded-full p-1.5 cursor-pointer hover:bg-green-700 transition-colors"
                      >
                        <Upload className="h-4 w-4 text-white" />
                        <input
                          id="profile-image-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </label>
                    )}
                  </div>

                  <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
                    {adminData?.getCurrentAdmin?.firstName} {adminData?.getCurrentAdmin?.lastName}
                  </h2>

                  <p className="text-gray-500 dark:text-gray-400">
                    {adminData?.getCurrentAdmin?.position || 'No position set'}
                  </p>

                  <div className="mt-3 flex flex-wrap gap-2 justify-center">
                    {adminData?.getCurrentAdmin?.roles?.map((role, index) => (
                      <Badge key={index} variant="outline" className="bg-green-50 text-green-800 dark:bg-green-900 dark:text-green-100">
                        {getRoleDisplayName(role)}
                      </Badge>
                    ))}
                  </div>

                  <Button
                    variant={isEditing ? "destructive" : "outline"}
                    className="mt-6 w-full"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? "Cancel Editing" : "Edit Profile"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Profile Details */}
          <div className="md:col-span-2">
            <Card className="dark:bg-black">
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="firstName" className="text-gray-700 dark:text-gray-300">First Name</Label>
                      {isEditing ? (
                        <Input
                          id="firstName"
                          name="firstName"
                          value={profile.firstName}
                          onChange={handleInputChange}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {profile.firstName || 'Not set'}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="lastName" className="text-gray-700 dark:text-gray-300">Last Name</Label>
                      {isEditing ? (
                        <Input
                          id="lastName"
                          name="lastName"
                          value={profile.lastName}
                          onChange={handleInputChange}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {profile.lastName || 'Not set'}
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator className="dark:border-gray-700" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1" />
                      <div>
                        <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">Email</Label>
                        {isEditing ? (
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={profile.email}
                            onChange={handleInputChange}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 dark:text-white font-medium">
                            {profile.email || 'Not set'}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1" />
                      <div>
                        <Label htmlFor="phone" className="text-gray-700 dark:text-gray-300">Phone Number</Label>
                        {isEditing ? (
                          <Input
                            id="phone"
                            name="phone"
                            value={profile.phone}
                            onChange={handleInputChange}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 dark:text-white font-medium">
                            {profile.phone || 'Not set'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator className="dark:border-gray-700" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <Briefcase className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1" />
                      <div>
                        <Label htmlFor="position" className="text-gray-700 dark:text-gray-300">Position</Label>
                        {isEditing ? (
                          <Input
                            id="position"
                            name="position"
                            value={profile.position}
                            onChange={handleInputChange}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 dark:text-white font-medium">
                            {profile.position || 'Not set'}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Building2 className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1" />
                      <div>
                        <Label htmlFor="department" className="text-gray-700 dark:text-gray-300">Department</Label>
                        {isEditing ? (
                          <Input
                            id="department"
                            name="department"
                            value={profile.department}
                            onChange={handleInputChange}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 dark:text-white font-medium">
                            {profile.department || 'Not set'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator className="dark:border-gray-700" />

                  <div className="flex items-start gap-3">
                    <CalendarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1" />
                    <div className="w-full">
                      <Label htmlFor="joinDate" className="text-gray-700 dark:text-gray-300">Join Date</Label>
                      {isEditing ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !profile.joinDate && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {profile.joinDate ? format(profile.joinDate, "PPP") : "Pick a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <div className="p-3">
                              <div className="flex gap-2 mb-2">
                                <Select 
                                  value={format(calendarDate, 'yyyy')}
                                  onValueChange={(year) => {
                                    const newDate = new Date(calendarDate);
                                    newDate.setFullYear(parseInt(year));
                                    setCalendarDate(newDate);
                                  }}
                                >
                                  <SelectTrigger className="w-[110px]">
                                    <SelectValue placeholder="Year" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from({ length: new Date().getFullYear() - 1970 + 1 }, (_, i) => (
                                      <SelectItem key={1970 + i} value={(1970 + i).toString()}>
                                        {1970 + i}
                                      </SelectItem>
                                    )).reverse()}
                                  </SelectContent>
                                </Select>
                                <Select
                                  value={format(calendarDate, 'MM')}
                                  onValueChange={(month) => {
                                    const newDate = new Date(calendarDate);
                                    newDate.setMonth(parseInt(month) - 1);
                                    setCalendarDate(newDate);
                                  }}
                                >
                                  <SelectTrigger className="w-[110px]">
                                    <SelectValue placeholder="Month" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from({ length: 12 }, (_, i) => {
                                      const month = new Date(0, i).toLocaleString('default', { month: 'long' });
                                      return (
                                        <SelectItem key={i + 1} value={(i + 1).toString().padStart(2, '0')}>
                                          {month}
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Calendar
                                mode="single"
                                selected={profile.joinDate}
                                onSelect={handleDateChange}
                                defaultMonth={calendarDate}
                                month={calendarDate}
                                onMonthChange={setCalendarDate}
                                initialFocus
                                fromYear={1970}
                                toYear={new Date().getFullYear()}
                                showHeader={false}
                                className="rounded-md border"
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {profile.joinDate ? format(new Date(profile.joinDate), "PPP") : 'Not set'}
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator className="dark:border-gray-700" />

                  <div>
                    <Label htmlFor="bio" className="text-gray-700 dark:text-gray-300">Bio</Label>
                    {isEditing ? (
                      <Textarea
                        id="bio"
                        name="bio"
                        value={profile.bio}
                        onChange={handleInputChange}
                        className="mt-1"
                        rows={4}
                      />
                    ) : (
                      <p className="mt-1 text-gray-900 dark:text-white font-medium whitespace-pre-line">
                        {profile.bio || 'No bio information'}
                      </p>
                    )}
                  </div>

                  {isEditing && (
                    <Button
                      onClick={saveProfile}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Save Profile
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonnelProfilePage;
